import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Drawer } from 'primeng/drawer';
import { Popover } from 'primeng/popover';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  interval,
  Observable,
  take,
  tap,
} from 'rxjs';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Sentry from '@sentry/browser';
import { PackhouseConnection, PermissionEnum } from '@app/generated/graphql';
import { Router } from '@angular/router';
import { PermissionService } from '@app/core/services/permission.service';
import { PackhouseConfirmationService } from '@app/account/packhouse-confirmation-dashboard/services/packhouse-confirmation.service';

@Component({
  selector: 'app-packhouse-connectivity',
  templateUrl: './packhouse-connectivity.component.html',
  standalone: false,
  providers: [PackhouseConfirmationService],
})
export class PackhouseConnectivityComponent implements OnInit {
  // dependencies
  destroyRef = inject(DestroyRef);
  private _packhouseConfirmationService = inject(PackhouseConfirmationService);
  private _messageService = inject(MessageService);
  private _router = inject(Router);
  permissionService = inject(PermissionService);

  packhouseConnections = signal<PackhouseConnection[]>([]);
  filteredPackhouseConnections = signal<PackhouseConnection[]>([]);

  layout = input<'menu' | 'standalone'>('standalone');

  // state properties
  drawerVisible = signal<boolean>(false);
  popoverVisible = signal<boolean>(false);
  lastRefreshedAt = signal<string>(new Date().toISOString());
  isLoading = signal<boolean>(true); // used for manual refresh
  isRefreshing = signal<boolean>(false); // used for polling (background refresh)

  // computed properties
  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.packhouseConnections()?.length > 6,
  );

  offlineCount = computed(
    () =>
      this.packhouseConnections()?.filter(
        (packhouseConnection: PackhouseConnection) =>
          !packhouseConnection.connected,
      ).length,
  );

  popover = viewChild<Popover>('packhouseConnectionsPopover');
  drawer = viewChild<Drawer>('packhouseConnectionsDrawer');

  // template access
  permissionEnum = PermissionEnum;

  constructor() {}

  ngOnInit(): void {
    this.fetchPackhouseConnections();
    this.setupRefetchTimer();
  }

  refreshPackhouseConnections(): void {
    this.fetchPackhouseConnections();
  }

  fetchPackhouseConnections(
    isBackground: boolean = false,
    callback?: () => void,
  ): void {
    if (!isBackground) {
      this.isLoading.set(true);
    } else {
      this.isRefreshing.set(true);
    }

    this._packhouseConfirmationService
      .fetchPackhouseConnections$()
      .pipe(
        take(1),
        tap((packhouseConnections: PackhouseConnection[]) => {
          const collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: 'base',
          });
          this.packhouseConnections.set(
            [...packhouseConnections].sort((a, b) =>
              collator.compare(
                a.foodBusinessOperator?.name ?? '',
                b.foodBusinessOperator?.name ?? '',
              ),
            ),
          );
          this.filteredPackhouseConnections.set(
            [...packhouseConnections].sort((a, b) =>
              collator.compare(
                a.foodBusinessOperator?.name ?? '',
                b.foodBusinessOperator?.name ?? '',
              ),
            ),
          );
          this.lastRefreshedAt.set(new Date().toISOString());
        }),
        catchError((error: Error) => this.handleError(error)),
        finalize(() => {
          this.isLoading.set(false);
          this.isRefreshing.set(false);
          callback?.();
        }),
      )
      .subscribe();
  }

  handleError(error: Error): Observable<never> {
    this._messageService.add({
      severity: 'error',
      summary: 'Error fetching packhouse connections',
      detail: error.message,
    });

    console.error(error);
    Sentry.captureException(error);

    return EMPTY;
  }

  onMenuClick(event: Event): void {
    this.popover().toggle(event);
  }

  onStandaloneClick(event: Event): void {
    if (this.isSidepanelLayout()) {
      this.drawerVisible.set(true);
      return;
    }

    this.popover().toggle(event);
  }

  setupRefetchTimer(): void {
    interval(300_000) // 5 minutes
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.drawerVisible() || this.popoverVisible()),
        tap(() => this.fetchPackhouseConnections(true)),
        catchError((error: Error) => this.handleError(error)),
      )
      .subscribe();
  }

  openPackhouseManager(): void {
    if (
      !this.permissionService.hasPermission(
        PermissionEnum.MasterfilesPackhouses,
      )
    ) {
      return;
    }

    this._router.navigate(['/account/masterfiles/packhouse-manager']);
    // hide the view
    this.popover().hide();
    this.drawerVisible.set(false);
  }
}
